import React, { useEffect, useState } from "react"
import dayjs from "dayjs"
import ModalComponent from "../Modal/Modal"
import Calendar from "../Calendar/Calendar"
import { formatMoney } from "../../utils/amount"
import { computeOwed } from "../../utils/partialPayment"
import { rescheduleDates } from "./rescheduleDates"

/**
 * WS 2074858. Reschedule a declined payment: the next attempt of the same payment (same amount,
 * billing account and payment number) on a new date; the declined attempt stays failed. The date
 * is the end of the program, the same day next week, or one agreed with the client (Dolly,
 * 2026-10-05).
 *
 * `payment` is the table row being rescheduled, `payments` every row of the client's schedule.
 * onConfirm receives { dueDate } as YYYY-MM-DD.
 */
const OPTIONS = [
  { value: 'endOfProgram', label: 'End of program' },
  { value: 'nextWeek', label: 'Same day next week' },
  { value: 'custom', label: 'Custom date' },
]

const shown = (date) => (date ? dayjs(date).format('MM/DD/YYYY') : '')

const ReschedulePaymentModal = ({ open, onClose, payment, payments, onConfirm }) => {
  const [option, setOption] = useState('endOfProgram')
  const [customDate, setCustomDate] = useState('')
  const dates = rescheduleDates({ payment, payments })

  // A fresh choice every time the window opens.
  useEffect(() => {
    if (!open) return
    setOption('endOfProgram')
    setCustomDate('')
  }, [open, payment?.id])

  const picked = option === 'custom'
    ? (customDate && dayjs(customDate).isValid() ? dayjs(customDate).format('YYYY-MM-DD') : '')
    : dates[option]

  return (
    <ModalComponent
      title="Reschedule Payment"
      modalButtonTitle="Reschedule"
      disableBtn={!picked}
      customOpenModal={open}
      onClose={onClose}
      functionButton={() => onConfirm({ dueDate: picked })}
    >
      <p className="text-sm text-text-secondary mb-4" data-testid="reschedule-hint">
        {`Payment #${payment?.pmt} (${formatMoney(computeOwed(payment))}) will be charged again on the date you pick, from the same billing account. The declined attempt stays failed.`}
      </p>

      <div className="flex flex-col gap-3">
        {OPTIONS.map(({ value, label }) => (
          <label key={value} className="flex items-center gap-3 text-sm text-text-primary cursor-pointer">
            <input
              type="radio"
              name="reschedule-option"
              value={value}
              checked={option === value}
              onChange={() => setOption(value)}
            />
            <span>{label}</span>
            {value !== 'custom' && <span className="text-text-secondary">{shown(dates[value])}</span>}
          </label>
        ))}
      </div>

      {option === 'custom' && (
        <div className="mt-4">
          <Calendar
            width="w-full"
            label="New date"
            value={customDate}
            onChange={(val) => setCustomDate(val)}
            isRequired={true}
            disablePast={true}
            timezone="system"
          />
        </div>
      )}
    </ModalComponent>
  )
}

export default ReschedulePaymentModal
